import { useSyncExternalStore } from "react";

/**
 * Whether a media query matches, kept current as the window changes.
 *
 * For the few layouts that differ in structure rather than in style — a list that
 * selects into a pane beside it on a tablet, and navigates to a record on a phone. A
 * class can hide a pane; it cannot change what tapping a row does.
 *
 * Falls back to "no match" where there is no `matchMedia` (tests, server rendering), so
 * the phone layout is the default everywhere a window cannot be asked.
 */
export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (onChange) => {
      const list = globalThis.matchMedia?.(query);
      list?.addEventListener("change", onChange);

      return () => list?.removeEventListener("change", onChange);
    },
    () => globalThis.matchMedia?.(query).matches ?? false,
    () => false,
  );
}

/**
 * A tablet held in landscape: wide enough for a list and a record side by side, not wide
 * enough for the desktop rail and its filters. `lg` up to `xl`.
 */
export const SPLIT_VIEW = "(min-width: 64rem) and (max-width: 79.999rem)";

/** Wide enough for a record to lay out beside its photo rather than under it. `md` up. */
export const WIDE_RECORD = "(min-width: 48rem)";
